import React, { useImperativeHandle, useMemo, useState } from 'react';
import Drawer from '@ui/drawer/Drawer';
import useDrawer from '@ui/drawer/DrawerRef';
import DataTable from '@ui/chart/DataTable';
import { formatSigned, formatValue } from '@ui/chart/chartFormat';
import Message from '@ui/message/Message';
import FormButton from '@ui/button/FormButton';
import TablePlaceholder from '@ui/placeholder/TablePlaceholder';
import { BiBookingCut, BiBookingReport, RevenueScope, VatBasis } from '@app/model/bi/Bi';
import { useBiBookings } from '@app/model/bi/service/BiHooks';
import { bookingColumns, bookingFooter, bookingTableRows, bookingTotals, BookingMeasure, cutLabel, isPickup } from './BiBookings.utilities';

// The bookings behind a number of a BI table (ROO2-284): a Screen Part of every BI report that opens the list (Channels first,
// grill D9). The drawer reads the bookings of one row (its channel keys, or one raw name) at the cut of the clicked column.

export type BiBookingsRequest = {
	report: BiBookingReport,
	companyHotelId: number | null,
	propertyGroupId: number | null,
	// The stay months of the page, YYYY-MM.
	stayFrom: string,
	stayTo: string,
	cut: BiBookingCut,
	measure: BookingMeasure,
	keys: string[],
	name: string | null,
	// ROO2-310: the Options switch of the page when the list opened, so the list adds up to the number of the table.
	includeOptions: boolean,
	// The row and the column as the table says them: "Booking.com", "PU 7 RN".
	rowTitle: string,
	columnTitle: string,
};

export type BiBookingsDrawerHandle = { open: (request: BiBookingsRequest) => void };

type Props = {
	scope: RevenueScope,
	vat: VatBasis,
	// More than one property in the scope: the list shows the property of each booking.
	manyProperties: boolean,
	propertyName: (companyHotelId: number) => string,
	// The Export menu of the list (Current view, All data), made by the page.
	exportMenu: (request: BiBookingsRequest, ready: boolean) => React.ReactNode,
};

const number = (v: number, signed: boolean) => signed ? formatSigned(v, 'number') : formatValue(v, 'number');
const money = (v: number | null, signed: boolean) => v === null ? '–' : (signed ? formatSigned(v, 'money') : formatValue(v, 'money'));

const BiBookingsDrawer = React.forwardRef<BiBookingsDrawerHandle, Props>(({ scope, vat, manyProperties, propertyName, exportMenu }, ref) => {
	const drawer = useDrawer();
	const [request, setRequest] = useState<BiBookingsRequest | null>(null);
	useImperativeHandle(ref, () => ({ open: next => { setRequest(next); drawer.open(); } }));
	const query = useBiBookings(request === null ? null : {
		report: request.report, companyHotelId: request.companyHotelId, propertyGroupId: request.propertyGroupId, stayFrom: request.stayFrom,
		stayTo: request.stayTo, cut: request.cut, keys: request.keys, name: request.name, includeOptions: request.includeOptions,
	});
	const data = query.data;
	const pu = request !== null && isPickup(request.cut);
	const columns = useMemo(() => request === null ? [] : bookingColumns(request.cut, manyProperties), [request, manyProperties]);
	const rows = useMemo(() => data === undefined ? [] : bookingTableRows(data.rows, scope, vat, propertyName), [data, scope, vat, propertyName]);
	const totals = useMemo(() => data === undefined ? null : bookingTotals(data.rows, scope, vat), [data, scope, vat]);
	const title = request === null ? 'Bookings' : `Bookings · ${request.rowTitle} · ${request.columnTitle}`;
	const ready = data !== undefined && !data.more && data.rows.length > 0;

	return <Drawer title={title} size="huge" ref={drawer.ref} onClose={() => setRequest(null)}>
		{request !== null && <div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<p className="text-sm text-gray-500">
					{pu ? `The bookings whose room nights or revenue changed between situation D-${request.cut.slice(2)} and today.`
						: `The bookings on the books for the period at ${cutLabel(request.cut) === 'Today' ? 'today' : cutLabel(request.cut)}.`}
					{request.cut === 'STLY' || request.cut === 'LY_ACTUAL' ? ' The stay dates are one year earlier.' : ''}
				</p>
				{exportMenu(request, ready)}
			</div>
			{query.isError
				? <Message state="error" title="The bookings did not load" flush>
					<div className="flex flex-wrap items-center gap-3">
						<span>Try again. If the problem stays, contact RoomPulse.</span>
						<FormButton state="white" size="small" onClick={() => void query.refetch()}>Try again</FormButton>
					</div>
				</Message>
				: data === undefined
					? <div aria-busy="true" aria-label="Loading the bookings"><TablePlaceholder /></div>
					: data.more
						? <Message state="warning" title="More than 5,000 bookings" flush>
							This number holds too many bookings to list. Choose one property, a Property group or fewer stay months.
						</Message>
						: data.rows.length === 0 || totals === null
							? <Message state="info" title="No booking behind this number" flush>
								{pu ? 'No booking changed in this period since the situation.' : 'No booking holds a room night of this row in the period at this cut.'}
							</Message>
							: <>
								<p className="text-sm text-gray-700" aria-live="polite">
									<span className="font-semibold text-gray-900">{totals.bookings} {totals.bookings === 1 ? 'booking' : 'bookings'}</span>
									{' · '}{number(totals.roomNights, pu)} RN · {money(totals.revenue, pu)}
									{pu && <span className="text-gray-500"> · {totals.up.bookings} up ({number(totals.up.roomNights, true)} RN), {totals.down.bookings} down ({number(totals.down.roomNights, true)} RN)</span>}
								</p>
								<DataTable columns={columns} rows={rows} footer={bookingFooter(totals)} ariaLabel={title}
									defaultSort={{ columnId: 'booked', direction: 'desc' }} />
							</>}
		</div>}
	</Drawer>;
});

export default BiBookingsDrawer;
